import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { IconCheck } from '@tabler/icons-react';
import { exhibitions } from './default';
import Skeleton from 'react-loading-skeleton';

function normalizeImageSrc(path = '') {
  if (!path) return path;
  if (/^(?:https?:)?\/\//i.test(path) || path.startsWith('data:') || path.startsWith('/')) {
    return path;
  }
  return `/${path}`;
}

function ImgSkel({
  src,
  alt,
  wrapperClass = 'relative overflow-hidden',
  imgClass = 'h-full w-full object-cover',
  fallback = '/placeholder.jpg',
}) {
  const [loaded, setLoaded] = useState(false);
  const [err, setErr] = useState(false);

  return (
    <div className={wrapperClass}>
      {!loaded && (
        <Skeleton className="absolute inset-0 h-full w-full" containerClassName="h-full w-full" />
      )}
      <img
        src={normalizeImageSrc(err ? fallback : src)}
        alt={alt}
        onLoad={() => setLoaded(true)}
        onError={() => {
          setErr(true);
          setLoaded(true);
        }}
        className={`${imgClass} transition-all duration-700 ease-in-out ${
          loaded ? 'opacity-100 scale-100' : 'opacity-0 scale-105'
        }`}
        loading="lazy"
      />
    </div>
  );
}

const KinArtGallery = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const navigationExhibitions = [...exhibitions].reverse();
  const requestedExhibitionId = searchParams.get('exhibitionId') || searchParams.get('id');
  const requestedSection = searchParams.get('section');

  const findExhibitionById = (targetId) => {
    if (!targetId) return null;
    const targetStringId = String(targetId);

    const exactMatch = exhibitions.find((ex) => String(ex.id) === targetStringId);
    if (exactMatch) return exactMatch;

    const numericTarget = Number(targetStringId);
    if (Number.isNaN(numericTarget)) return null;

    return exhibitions.find((ex) => Number(ex.id) === numericTarget) || null;
  };

  const [activeId, setActiveId] = useState(
    () =>
      findExhibitionById(requestedExhibitionId)?.id ||
      navigationExhibitions[0]?.id ||
      exhibitions[0]?.id ||
      '',
  );
  const activeExhibition = exhibitions.find((ex) => ex.id === activeId) || exhibitions[0];

  useEffect(() => {
    if (!requestedExhibitionId) return;
    const matchedExhibition = findExhibitionById(requestedExhibitionId);
    if (matchedExhibition) {
      setActiveId(matchedExhibition.id);
    }
  }, [requestedExhibitionId]);

  useEffect(() => {
    const shouldFocusGallery = requestedSection === 'kinartgallery' || !!requestedExhibitionId;
    if (!shouldFocusGallery) return;

    requestAnimationFrame(() => {
      const section = document.getElementById('kinartgallery');
      if (section) {
        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  }, [requestedSection, requestedExhibitionId]);

  const getDetailHref = (id) => {
    navigate(`/about/exhibition/${id}`);
  };

  return (
    <section id="kinartgallery" aria-labelledby="kinartgallery-title" className="relative">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <div className="flex items-end justify-between">
          <h3 className="text-xl font-semibold text-gray-800">Art Gallery Retail</h3>
          <span className="text-xs sm:text-sm text-kin-text">New Curated</span>
        </div>

        <div className="relative mt-3">
          {/* Navigation: Dibuat seperti Tab yang menempel ke konten */}
          <div className="flex gap-2 overflow-x-auto px-1 pb-2 snap-x lg:grid lg:grid-cols-6 lg:gap-2 lg:overflow-visible lg:px-2 lg:pb-0 lg:snap-none">
            {navigationExhibitions.map((ex) => {
              const isActive = activeId === ex.id;
              const tabColor = ex.poster_color || '#F58136';

              return (
                <button
                  key={ex.id}
                  onClick={() => setActiveId(ex.id)}
                  className={`group relative z-10 -mb-px min-w-[160px] snap-start rounded-t-2xl border border-b-0 px-3 py-3 text-left transition-all duration-300 lg:min-w-0 lg:px-4 ${
                    isActive
                      ? 'border-gray-200 bg-white shadow-sm'
                      : 'border-transparent bg-gray-50/70 hover:bg-gray-100'
                  }`}>
                  <span
                    style={{
                      color: isActive ? tabColor : undefined,
                    }}
                    className={`block text-[10px] font-bold uppercase tracking-widest ${
                      isActive ? '' : 'text-gray-400 group-hover:text-primary-main'
                    }`}>
                    {ex.subtitle}
                  </span>
                  <span
                    className={`mt-1 block text-sm font-bold uppercase ${
                      isActive ? 'text-kin-textdark' : 'text-gray-600'
                    }`}>
                    {ex.label}
                  </span>
                  {isActive && (
                    <span
                      style={{
                        backgroundColor: tabColor,
                      }}
                      className="absolute top-2 right-2 inline-flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-bold text-white">
                      <IconCheck className="h-3 w-3 text-white" />
                    </span>
                  )}

                  {/* Garis penutup border bawah agar terlihat menyatu */}
                  {isActive && (
                    <div className="absolute -bottom-[2px] left-0 right-0 h-[3px] bg-white z-20" />
                  )}
                </button>
              );
            })}
          </div>

          {/* Main Display Area */}
          <div className="grid gap-6 p-4 sm:p-6 lg:gap-12 lg:grid-cols-12 rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <div className="lg:col-span-5">
              <ImgSkel
                src={activeExhibition.poster_image}
                alt={activeExhibition.label}
                wrapperClass="relative w-full aspect-[891/1080] overflow-hidden rounded-2xl border border-gray-100 bg-gray-50"
                imgClass="h-full w-full object-cover object-top"
              />
            </div>

            <div className="lg:col-span-5 flex flex-col">
              <div className="space-y-4 mb-6">
                <div className="inline-flex items-center gap-2 rounded-full bg-primary-main/10 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-primary-main">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-main opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-primary-main"></span>
                  </span>
                  Active Display
                </div>
                <div className="flex flex-row items-end gap-3">
                  <h3 className="text-3xl sm:text-4xl font-bold text-kin-textdark lg:text-5xl uppercase">
                    {activeExhibition.label}
                  </h3>
                  <span className="text-lg text-kin-text italic">{activeExhibition.sublabel}</span>
                </div>

                <p className="text-base leading-relaxed text-kin-textdark italic font-serif">
                  "{activeExhibition.desc.split('.')[0]}."
                </p>
                <p className="text-base leading-relaxed text-kin-textdark">
                  {activeExhibition.desc.split('.').slice(1).join('.')}
                </p>
              </div>

              <button
                onClick={() => getDetailHref(activeExhibition.id)}
                style={{
                  backgroundColor: activeExhibition.poster_color
                    ? activeExhibition.poster_color
                    : '#F58136',
                }}
                className={`w-full rounded-2xl py-4 text-sm font-bold text-white transition-all hover:shadow-lg active:scale-[0.98]`}>
                See Detail Exhibition
              </button>
            </div>

            <div className="lg:col-span-2 flex flex-col items-center border-t pt-5 lg:border-t-0 lg:border-l border-gray-200 lg:pl-6 lg:pt-0">
              <div className="">
                <p className="text-[10px] font-black uppercase tracking-[0.1em] text-kin-textdark mb-6 text-center">
                  The Artists
                </p>

                <div className="flex gap-4 overflow-x-auto pb-1 lg:pb-0 lg:flex-col lg:gap-6 lg:overflow-visible">
                  {activeExhibition.collaborators.map((artist, idx) => (
                    <div
                      key={idx}
                      className="group relative flex min-w-[96px] flex-col items-center lg:min-w-0">
                      <a
                        href={artist.href}
                        target="_blank"
                        rel="noreferrer"
                        className="relative h-20 w-20 sm:h-24 sm:w-24 lg:h-28 lg:w-28 overflow-hidden rounded-full border border-gray-100 bg-gray-50 p-1 transition-all duration-500 group-hover:border-primary-main group-hover:shadow-lg">
                        <ImgSkel
                          src={artist.avatar}
                          alt={artist.name}
                          imgClass="h-full w-full rounded-full object-cover transition-all duration-700 group-hover:scale-110"
                        />
                      </a>
                      <span className="mt-2 font-serif text-xs sm:text-sm tracking-wide text-kin-textdark opacity-80 transition-opacity group-hover:opacity-100 text-center">
                        {artist.name}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default KinArtGallery;
